import { Component, Input, OnInit } from '@angular/core';
import { ChartService } from '@app/data/services/chartService';
import {
  BasicBarChartConfig,
  BasicCartesianChartConfig,
  BasicChartDataSource,
} from '@app/data/interface/widget-config';
import { mapValueForChart } from '../helpers/widgets';
import { forkJoin } from 'rxjs';
import { take } from 'rxjs/operators';
import moment from 'moment-timezone';
import { EChartsOption, LineSeriesOption, SeriesOption } from 'echarts';
import { FilterService } from '@app/data/services/filterService';
import { SitesService } from '@app/data/services/sitesService';
import { TopLevelFormatterParams } from 'echarts/types/dist/shared';
import { BaseFilteredEnvironmentalDataChartComponent } from '../base-filtered-environmental-data-chart-component';

/**
 *
 */

@Component({
  selector: 'app-macrophytes-bar-chart',
  templateUrl: './macrophytes-bar-chart.component.html',
  styleUrls: ['./macrophytes-bar-chart.component.scss'],
})
export class MacrophytesBarChartComponent extends BaseFilteredEnvironmentalDataChartComponent {
  @Input() config!: BasicBarChartConfig;
  @Input() startDateOverride?: string;
  @Input() endDateOverride?: string;
  @Input() chartOptions: EChartsOption | null = null;

  ngOnInit() {
    super.ngOnInit();
    this.id = `basic-bar-chart-${this.config.dataConfig.widgetId}`;
    this.chartTitle = this.config.dataConfig.title || '';
    this.chartSubtitle = this.config.dataConfig.subtitle || '';
    this.domain = this.config.dataConfig.domain || '';

    // First load.
    this.setWidgetData();
  }

  handleSiteUpdate = (site: any) => {
    if (site) {
      this.setWidgetData();
    }
  };

  /**
   * Clear any displayed chart data so the UI does not show stale results while
   * new data is being fetched. Implements the abstract contract from the base class.
   */
  protected clearChartData(): void {
    this.chartOptions = null;
    this.hasData = false;
  }

  /**
   * Populate responses for the chart
   *
   * If showBoundary is set in the config, center bars on ticks for bar charts, otherwise use no boundary gap
   *
   * @param newChartOptions ECharts options object to populate
   * @param queryResponses Raw responses for each data source
   * @param xAxisData Optional array to populate with unique x-axis category values (date_time strings)
   * @param dateFormat Moment.js format string used to render x-axis labels (defaults to 'DD/MM/yyyy').
   */
  populateResponses(
    newChartOptions: EChartsOption,
    queryResponses: any,
    xAxisData: any[] = [],
    dateFormat: string = '[Q]Q YYYY'
  ): void {
    queryResponses.forEach((seriesData: any, index: number) => {
      if (seriesData.data.length > 0) {
        this.hasData = true;
      }
      seriesData.data.forEach((dataPoint: any) => {
        const yearAndQuarter = moment(dataPoint.date_time).format(dateFormat);
        if (!xAxisData.includes(yearAndQuarter)) {
          xAxisData.push(yearAndQuarter);
        }
      });
    });

    if (!this.hasData) {
      this.chartOptions = {
        color: ['#6AB6B0'],
        title: {
          text: 'No data is available for the selected date range.',
        },
      };
    } else {
      // Set the data.
      // xAxisData.sort((a: string, b: string) => (a > b ? 1 : -1));
      queryResponses.forEach((seriesData: any, index: number) => {
        const dataSource: BasicChartDataSource = this.config.dataConfig.dataSources[index];

        // use the create a series per entity

        // Group observations by entity_id and date_time
        const groupedData: { [entityId: number]: { [yearAndQuarter: string]: number } } = {};

        seriesData.data.forEach((observation: any) => {
          const entityId = observation.observation_entity_id;
          const yearAndQuarter = moment(observation.date_time).format(dateFormat);
          const sum = observation.sum;

          if (!groupedData[entityId]) {
            groupedData[entityId] = {};
          }

          // If we already have a value for this quarter, we should probably sum them.
          // Assuming that's the desired behavior.
          if (groupedData[entityId][yearAndQuarter]) {
            groupedData[entityId][yearAndQuarter] += sum;
          } else {
            groupedData[entityId][yearAndQuarter] = sum;
          }
        });

        newChartOptions.series = seriesData.entities.map((ent: any) => ({
          name: ent.name,
          type: 'bar',
          stack: 'total',
          barWidth: '60%',
          data: xAxisData.map((yearAndQuarter: string) =>
            mapValueForChart(groupedData[ent.id]?.[yearAndQuarter], true, null)
          ),
        }));

        newChartOptions.tooltip = {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow',
          },
          formatter: (params: any) => {
            if (!Array.isArray(params) || !params.length) {
              return '';
            }

            const seriesLines = params
              .filter((p: any) => {
                const value = typeof p.value === 'number' ? p.value : 0;
                return value > 0;
              })
              .map((p: any) => {
                const value = typeof p.value === 'number' ? p.value : 0;
                return `<tr><td  style="text-align: left;">${p.marker} ${p.seriesName}</td><td>${value.toFixed(
                  2
                )}%</td></tr>`;
              })
              .join('');
            return `<table>${seriesLines}</table>`;
          },
        };
        newChartOptions.legend = {
          show: false,
          top: 0,
          data: seriesData.entities.map((ent: any) => ({
            name: ent.name,
            overflow: 'truncate',
          })),
        };
      });

      newChartOptions.grid = {
        top: '3%',
        left: '3%',
        right: '3%',
        bottom: '3%',
        containLabel: true,
      };

      newChartOptions.xAxis = {
        type: 'category',
        boundaryGap: this.config.dataConfig.showBoundary,
        data: xAxisData.slice(),
        axisLabel: {
          rotate: 45,
          rich: {
            bold: {
              fontWeight: 'bold',
            },
          },
          formatter: (value: any) => {
            const label = typeof value === 'number' ? xAxisData[value] : value;
            const mm = moment(label, dateFormat, true).isValid() ? moment(label, dateFormat, true) : moment(label);
            if (!mm.isValid()) return '';
            return mm.format(dateFormat);
          },
        },
      };

      newChartOptions.yAxis = {
        type: 'value',
        name: 'Cover %',
      };

      this.chartOptions = newChartOptions;
    }
  }

  /**
   * Set the data for the chart
   */
  setWidgetData() {
    const newChartOptions: EChartsOption = this.config.echartsConfig;
    const xAxisData: any[] = [];
    this.hasData = false;
    this.chartOptions = null;

    // If we don't have any data sources to look at, exit early.
    const preserveConfiguredLabels = Boolean(this.config?.dataConfig?.preserveConfiguredDataSourceLabels);
    const applicableDataSources = this.selectDataSources(this.config.dataConfig.dataSources, {
      preserveConfiguredLabels,
    });

    // If there's nothing to display....
    if (applicableDataSources.length === 0) {
      return;
    }

    // Get the data
    forkJoin(
      applicableDataSources.map((source: any) =>
        this.chartService
          .getEnvironmentalDataForSourceAndAnalyteByDate(
            source.environmentalDataSourceIdentifier,
            source.analyteIdentifier,
            moment(this.startDate).format('YYYY-MM-DD'),
            moment(this.endDate).format('YYYY-MM-DD'),
            this.startDate === this.endDate ? 'hour' : 'day'
          )
          .pipe(take(1))
      )
    ).subscribe({
      next: (queryResponses: any) => this.populateResponses(newChartOptions, queryResponses, xAxisData),
      error: (err: any) => {
        console.error(err);
        this.chartOptions = {
          color: ['#6AB6B0'],
          title: {
            text: 'No data is available.',
          },
        };
      },
    });
  }
}
